import { getAccessToken } from '../authentication/supabaseService';
import { supabase } from '../authentication/supabaseService';


export interface BackendUser {
  preferred_language?: 'nl' | 'en' | null;
  managed_customer?: boolean;
  must_set_password?: boolean;
  demoContext?: { ownerId: string; restaurantIds: string[] };
  id: string;
  email: string;
  subscription_status: string;
  created_at: string;
  role: string;
  created_by?: string;
  receive_price_alert_emails?: boolean;
  receive_weekly_reports_emails?: boolean;
  receive_staff_signup_emails?: boolean | null;
  name?: string;
  profile_picture_url?: string;
  admin_user?: string;
  accountSettings?: string[];
  permissions: string[];
  access_request?: 'pending' | 'approved' | 'rejected' | null;
  restaurant_role?: string | null;
  /** Set by /api/users/me only. While impersonating this describes the CLIENT, so it is false. */
  is_super_admin?: boolean;
  /** Set by /api/users/me only. The real person behind the session — true while impersonating. */
  is_super_admin_identity?: boolean;
  /** Present only during verified impersonation; identity remains true in either view. */
  impersonation_view?: 'admin' | 'customer';
}


/**
 * Fetch the backend user profile from `/me` endpoint
 */
export async function fetchUserProfile(): Promise<BackendUser> {
    const token = await getAccessToken();

  const res = await fetch('/api/users/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch profile: ${res.statusText}`);
  }
  return (await res.json()) as BackendUser;
}


/**
 * delete the user from the backend
 */
export async function deleteUser(): Promise<void> {
    const token = await getAccessToken();
    const res = await fetch('/api/users/delete', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to delete user: ${res.statusText}`);
  }


/**
 * delete the user from the backend
 */
export async function deleteSupabaseUser(): Promise<void> {
    const token = await getAccessToken();
    const res = await fetch('/api/users/deleteSupabaseUser', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to delete user: ${res.statusText}`);
}

/**
 * Update the user's price alert email preference
 */
export async function updatePriceAlertPreference(receivePriceAlertEmails: boolean): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/users/price-alert-preference', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receivePriceAlertEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update price alert preference: ${res.statusText}`);
}

/**
 * Update a staff member's price alert email preference (admin only)
 */
export async function updateStaffPriceAlertPreference(
  staffId: string,
  receivePriceAlertEmails: boolean
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/users/staff/${staffId}/price-alert-preference`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receivePriceAlertEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update staff price alert preference: ${res.statusText}`);
}

/**
 * Update the user's weekly reports email preference
 */
export async function updateWeeklyReportsPreference(receiveWeeklyReportsEmails: boolean): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/users/weekly-reports-preference', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receiveWeeklyReportsEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update weekly reports preference: ${res.statusText}`);
}

/**
 * Update a staff member's weekly reports email preference (admin only)
 */
export async function updateStaffWeeklyReportsPreference(
  staffId: string,
  receiveWeeklyReportsEmails: boolean
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/users/staff/${staffId}/weekly-reports-preference`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receiveWeeklyReportsEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update staff weekly reports preference: ${res.statusText}`);
}

/**
 * Update the user's staff signup email preference
 */
export async function updateStaffSignupEmailPreference(receiveStaffSignupEmails: boolean): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/users/staff-signup-email-preference', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receiveStaffSignupEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update staff signup email preference: ${res.statusText}`);
}

/**
 * Update a staff member's staff signup email preference (admin only)
 */
export async function updateStaffStaffSignupEmailPreference(
  staffId: string,
  receiveStaffSignupEmails: boolean
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/users/staff/${staffId}/staff-signup-email-preference`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ receiveStaffSignupEmails }),
  });
  if (!res.ok) throw new Error(`Failed to update staff signup email preference: ${res.statusText}`);
}

/**
 * Update the user's profile (name and/or profile picture)
 */
export async function updateUserProfile(
  name?: string,
  profilePicture?: File
): Promise<BackendUser> {
  const token = await getAccessToken();
  const formData = new FormData();

  if (name !== undefined) {
    formData.append('name', name);
  }

  if (profilePicture) {
    formData.append('profilePicture', profilePicture);
  }

  const res = await fetch('/api/users/profile', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) throw new Error(`Failed to update profile: ${res.statusText}`);
  return (await res.json()) as BackendUser;
}

/**
 * Initiate email change in Supabase auth (requires confirmation)
 */
export async function initiateEmailChange(newEmail: string): Promise<void> {

  const redirectUrl = `${process.env.REACT_APP_FRONTEND_URL}/login`;

  const { error } = await supabase.auth.updateUser({
    email: newEmail
  }, { emailRedirectTo: redirectUrl });

  if (error) {
    throw new Error(`Failed to initiate email change: ${error.message}`);
  }
}

/**
 * Sync confirmed email from Supabase to backend database
 */
export async function syncEmailToBackend(): Promise<BackendUser> {
  const token = await getAccessToken();

  const res = await fetch('/api/users/email', {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error(`Failed to sync email: ${res.statusText}`);
  return (await res.json()) as BackendUser;
}

/** Complete managed-customer onboarding; the server enforces identity and password policy. */
export async function setInitialPassword(password: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch('/api/users/initial-password', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'initial_password_failed');
  }
}
